import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Bell,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  MessageSquare,
  DollarSign,
  FileUp,
  Briefcase,
  Info,
  Filter,
  ChevronLeft,
  ChevronRight,
  CheckCheck,
} from "lucide-react";
import { notificationsApi, PaginatedResponse } from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";

interface Notification {
  _id: string;
  type: string;
  title: string;
  body: string | null;
  link: {
    entity: string | null;
    entityId: string | null;
    url: string | null;
  } | null;
  read: boolean;
  readAt: string | null;
  createdAt: string;
}

const TYPE_ICONS: Record<string, { icon: React.FC<any>; color: string; bg: string }> = {
  task_assigned: { icon: ClipboardCheck, color: "text-blue-600", bg: "bg-blue-100" },
  approval_needed: { icon: CheckCircle2, color: "text-amber-600", bg: "bg-amber-100" },
  deadline_approaching: { icon: Clock, color: "text-red-600", bg: "bg-red-100" },
  message_received: { icon: MessageSquare, color: "text-purple-600", bg: "bg-purple-100" },
  payment_received: { icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-100" },
  document_uploaded: { icon: FileUp, color: "text-indigo-600", bg: "bg-indigo-100" },
  matter_updated: { icon: Briefcase, color: "text-blue-600", bg: "bg-blue-100" },
  system: { icon: Info, color: "text-slate-600", bg: "bg-slate-100" },
};

const typeOptions = [
  { value: "", label: "All Types" },
  { value: "task_assigned", label: "Task Assigned" },
  { value: "approval_needed", label: "Approval Needed" },
  { value: "deadline_approaching", label: "Deadline" },
  { value: "message_received", label: "Message" },
  { value: "payment_received", label: "Payment" },
  { value: "document_uploaded", label: "Document" },
  { value: "matter_updated", label: "Matter Update" },
  { value: "system", label: "System" },
];

const readOptions = [
  { value: "", label: "All" },
  { value: "false", label: "Unread" },
  { value: "true", label: "Read" },
];

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getTimeGroup(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0 && now.getDate() === date.getDate()) return "Today";
  if (days <= 1 && now.getDate() - date.getDate() === 1) return "Yesterday";
  if (days < 7) return "This Week";
  return "Older";
}

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [typeFilter, setTypeFilter] = useState("");
  const [readFilter, setReadFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (typeFilter) params.type = typeFilter;
      if (readFilter) params.read = readFilter;

      const res = await notificationsApi.list(params);
      const data = res.data as PaginatedResponse<Notification>;
      setNotifications(data.data);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, typeFilter, readFilter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkRead = async (id: string) => {
    try {
      await notificationsApi.markRead(id);
      setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n)));
    } catch (err) {
      console.error("Failed to mark as read:", err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true, readAt: new Date().toISOString() })));
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  // Group notifications by time
  const grouped = notifications.reduce<Record<string, Notification[]>>((acc, n) => {
    const group = getTimeGroup(n.createdAt);
    if (!acc[group]) acc[group] = [];
    acc[group].push(n);
    return acc;
  }, {});

  const groupOrder = ["Today", "Yesterday", "This Week", "Older"];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Notifications</h1>
          <p className="text-sm text-slate-500 mt-1">
            {pagination.total} notification{pagination.total !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={handleMarkAllRead}
          className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
        >
          <CheckCheck className="h-5 w-5" />
          Mark All Read
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="w-56">
            <SearchableSelect
              options={typeOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              value={typeFilter}
              onChange={(val) => {
                setTypeFilter(val || "");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              placeholder="All Types"
              icon={Filter}
              clearable
            />
          </div>
          <div className="w-40">
            <SearchableSelect
              options={readOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              value={readFilter}
              onChange={(val) => {
                setReadFilter(val || "");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              placeholder="All"
              clearable
            />
          </div>
        </div>
      </div>

      {/* Notification List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500">
            <Bell className="h-12 w-12 mb-3 text-slate-300" />
            <p className="font-medium text-lg">No notifications</p>
            <p className="text-sm mt-1">You're all caught up!</p>
          </div>
        ) : (
          <>
            {groupOrder.map((group) => {
              const items = grouped[group];
              if (!items || items.length === 0) return null;
              return (
                <div key={group}>
                  <div className="px-6 py-2 bg-slate-50 border-b border-slate-200">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {group}
                    </h3>
                  </div>
                  {items.map((n, i) => {
                    const typeInfo = TYPE_ICONS[n.type] || TYPE_ICONS.system;
                    const Icon = typeInfo.icon;
                    return (
                      <motion.div
                        key={n._id}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.02 }}
                        className={`flex items-start gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                          !n.read ? "bg-brand-light/30" : ""
                        }`}
                      >
                        <div className={`p-2 rounded-lg ${typeInfo.bg}`}>
                          <Icon className={`h-5 w-5 ${typeInfo.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className={`text-sm ${!n.read ? "font-semibold text-slate-800" : "text-slate-700"}`}>
                                {n.title}
                              </p>
                              {n.body && (
                                <p className="text-sm text-slate-500 mt-0.5">{n.body}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-slate-400">{timeAgo(n.createdAt)}</span>
                              {!n.read && (
                                <button
                                  onClick={() => handleMarkRead(n._id)}
                                  className="text-xs font-medium px-2 py-1 rounded hover:bg-brand-light transition-colors"
                                  style={{ color: "var(--color-primary)" }}
                                >
                                  Mark read
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              );
            })}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={pagination.page <= 1}
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                    className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                    className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
